import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {marketDriversUrl, marketSeriesUrl, marketUrl} from "../services/categoryProfileUrls";
import type {MarketDriver, MarketIndex, SourceRegistry} from "../types/marketTypes";
import {done, failed} from "./notices";

export function useMarketQuery(databagId: number, path: string) {
    const url = marketUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<MarketIndex>(url)).data,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Driver writes, each with its notice; removal and weight changes offer Undo. */
export function useMarketActions(databagId: number, path: string) {
    const qc = useQueryClient();
    return useMemo(() => {
        const key = [marketUrl(databagId, path)];
        const refreshRegistry = () => void qc.invalidateQueries({queryKey: [marketSeriesUrl(databagId)]});
        const write = async (method: 'post' | 'patch' | 'delete', url: string, data?: unknown) => {
            const res = await queryHttp.request<MarketIndex>({method, url, data});
            qc.setQueryData(key, res.data);
            refreshRegistry();
            return res.data;
        };
        const add = (seriesId: number, weight: number) => write('post', marketDriversUrl(databagId, path), {seriesId, weight});

        return {
            confirm: async (d: MarketDriver) => {
                try {
                    const res = await add(d.series.id, d.weight);
                    done(`${d.series.name} confirmed as a driver.`,
                        () => write('delete', marketDriversUrl(databagId, path, res.id)));
                } catch (e) {
                    failed(e);
                }
            },
            add: async (seriesId: number, name: string) => {
                try {
                    const res = await add(seriesId, 0.5);
                    done(`${name} added as a driver.`, () => write('delete', marketDriversUrl(databagId, path, res.id)));
                } catch (e) {
                    failed(e);
                }
            },
            setWeight: async (d: MarketDriver, weight: number) => {
                if (!d.mappingId || weight === d.weight) return;
                try {
                    await write('patch', marketDriversUrl(databagId, path, d.mappingId), {weight});
                    done('Weight updated.', () => write('patch', marketDriversUrl(databagId, path, d.mappingId!), {weight: d.weight}));
                } catch (e) {
                    failed(e);
                }
            },
            remove: async (d: MarketDriver) => {
                if (!d.mappingId) return;
                try {
                    const res = await write('delete', marketDriversUrl(databagId, path, d.mappingId));
                    const restore = res.restore;
                    done(`${d.series.name} removed.`, restore ? () => add(restore.seriesId, restore.weight) : undefined);
                } catch (e) {
                    failed(e);
                }
            },
            /** A customer's own series (CSV with period,value rows). Resolves once stored. */
            upload: async (name: string, unit: string, currency: string, file: File) => {
                const form = new FormData();
                form.append('name', name);
                form.append('unit', unit);
                form.append('currency', currency);
                form.append('file', file);
                try {
                    // queryHttp defaults to JSON; multipart keeps the file.
                    await queryHttp.post<SourceRegistry>(marketSeriesUrl(databagId), form,
                        {headers: {'Content-Type': 'multipart/form-data'}});
                    void qc.invalidateQueries({queryKey: key});
                    done(`${name} uploaded.`);
                } catch (e) {
                    failed(e);
                    throw e;
                }
            },
        };
    }, [qc, databagId, path]);
}

export type MarketActions = ReturnType<typeof useMarketActions>;
